import  { useEffect, useRef, useState } from 'react';
import CitySelector from '../components/CitySelector';
import { IoIosArrowBack } from 'react-icons/io';
import AnimatedWrapper from '../components/AnimatedWrapper';
import { useNavigate } from 'react-router-dom';
import ProgressBar from '../components/ProgressBar';
import { useUser } from '../context/useInfo';
import amplitude from '../utils/amplitude';

export default function Page10() {
  const [city, setCity] = useState(null);
  const [country, setCountry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFocused, setIsFocused] = useState(false);
  const typingTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const { updateUserData } = useUser();

  useEffect(() => {
    // установка переменной vh для iOS-фикс
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

        if(process.env.NODE_ENV === "production"){
              amplitude.track('Quiz Step Open', {
            step_number: 10,
          });
            }
    setVH();
    window.addEventListener('resize', setVH);
    return () => window.removeEventListener('resize', setVH);
  }, []);

  useEffect(() => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;

          try {
            const res = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&accept-language=en`
            );
            const data = await res.json();

            const detectedCity =
              data.address.city ||
              data.address.town ||
              data.address.village ||
              data.address.hamlet;

            const detectedCountry = data.address.country;

            if (detectedCity && detectedCountry) {
              setCity(detectedCity);
              setCountry(detectedCountry);
              setTimeout(() => setLoading(false), 2000);
            }
          } catch (err) {
            console.warn('Ошибка получения адреса по координатам', err);
            setLoading(false);
          }
        },
        (error) => {
          console.warn('Ошибка геолокации:', error.message);
          setLoading(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0,
        }
      );
    } else {
      setLoading(false);
    }
  }, []);

  function getRandomIntFrom3To8(x1, x2) {
    return Math.floor(Math.random() * (x1 - x2 + 1)) + x2;
  }

  const onCityChange = (val) => {
    setCity(val);
    setTimeout(() => {
      setIsFocused(false);
      setLoading(false);
    }, 2000);
  };
  const onCityInputChange = (input) => {
    if (input && input.length > 0) {
      setLoading(true);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        setLoading(false);
      }, 1000);
    } else {
      setLoading(false);
    }
  };

  const onCityBlur = () => {
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    setTimeout(() => {
      setIsFocused(false);
      setLoading(false);
    }, 2000);
  };

  const nextStep = () => {
    updateUserData({
      location: `${city}, ${country}`,
    });
    if(process.env.NODE_ENV === "production"){

      amplitude.track('Quiz Step Complete', {
        step_number: 10,
        answers: {
          location: `${city}, ${country}`,
        },
      });
    }
    navigate('/quiz11');
  };
  const stepBack = () => {
    updateUserData({ location: '' });
    navigate('/quiz9');
  };

  return (
    <AnimatedWrapper>
      <div
        className="flex flex-col px-4"
        style={{ height: 'calc(var(--vh, 1vh) * 100)' }}
      >
        {/* Верхний контент - растягивается, прижимается к верху */}
        <div className="flex flex-col flex-grow">
          {/* Top bar with back arrow and progress */}
          <div className="flex items-center space-x-2 pt-4 mb-6">
            <button className="text-gray-600" onClick={stepBack}>
              <IoIosArrowBack size={24} />
            </button>
            <ProgressBar />
          </div>

          {/* Title and subtitle */}
          <h2 className="text-3xl font-bold mb-2 text-center">I live in…</h2>
          <p className="text-sm text-gray-500 mb-4 text-left">
            Your local climate, air quality, and sun exposure impact your skin. We
            use your location to tailor our recommendations.
          </p>

          {/* City selector */}
          <CitySelector
            onChange={onCityChange}
            onInputChange={onCityInputChange}
            onBlur={onCityBlur}
            onFocus={() => setIsFocused(true)}
            defaultCity={city && country ? `${country}, ${city}` : ''}
          />

          {/* Metrics */}
          <div className="flex justify-between text-center mb-6 mt-6">
            {/* UV index */}
            <div className="flex-1 mx-1 bg-gray-50 rounded-xl p-3 flex flex-col items-center justify-center text-center">
              {/* svg omitted for brevity */}
              <p className="text-sm text-gray-500">UV index</p>
              {isFocused || loading ? (
                <svg
                  className="animate-spin h-6 w-6 text-gray-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  />
                </svg>
              ) : city ? (
                <p className="text-lg font-semibold">{getRandomIntFrom3To8(8, 3)}</p>
              ) : (
                <p className="text-lg font-semibold text-gray-400">-</p>
              )}
            </div>

            {/* Humidity */}
            <div className="flex-1 mx-1 bg-gray-50 rounded-xl p-3 flex flex-col items-center justify-center text-center">
              {/* svg omitted */}
              <p className="text-sm text-gray-500">Humidity</p>
              {isFocused || loading ? (
                <svg
                  className="animate-spin h-6 w-6 text-gray-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  ></path>
                </svg>
              ) : city ? (
                <p className="text-lg font-semibold">{getRandomIntFrom3To8(60, 40)}%</p>
              ) : (
                <p className="text-lg font-semibold text-gray-400">-</p>
              )}
            </div>

            {/* Pollution */}
            <div className="flex-1 mx-1 bg-gray-50 rounded-xl p-3 flex flex-col items-center justify-center text-center">
              {/* svg omitted */}
              <p className="text-sm text-gray-500">Pollution</p>
              {isFocused || loading ? (
                <svg
                  className="animate-spin h-6 w-6 text-gray-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  ></path>
                </svg>
              ) : city ? (
                <p className="text-lg font-semibold">{getRandomIntFrom3To8(60, 40)}%</p>
              ) : (
                <p className="text-lg font-semibold text-gray-400">-</p>
              )}
            </div>
          </div>
        </div>

        {/* Кнопка прижата вниз */}
        <div className="mt-auto mb-4">
          <button
            className="w-full bg-pinkCustom text-white py-3 rounded-full font-semibold text-lg"
            onClick={nextStep}
          >
            Next
          </button>
        </div>
      </div>
    </AnimatedWrapper>
  );
}
